import {
	isValidPhoneE164 as isValidPhoneE164Plain,
	normalizePhoneToE164,
	parsePhone,
} from "@bavard/utils";
// `/max` metadata matches the shared `@bavard/utils` parser — min metadata
// would let `isValid()` degrade to a length-only check.
import type { PhoneNumber as LibPhoneNumber } from "libphonenumber-js/max";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { z } from "zod";
import {
	PhoneE164Schema as BrandedPhoneE164Schema,
	createPhoneE164,
	type PhoneE164,
} from "./ids/phone-e164";
import {
	createWhatsAppWaIdDigits,
	type WhatsAppWaIdDigits,
} from "./ids/whatsapp-wa-id";

// Re-export types for convenience
export type { PhoneE164 } from "./ids/phone-e164";
// Re-export the structural (regex-only) E.164 schema so callers that only need
// fast structural validation (e.g. AI-tool filter/lookup fields) can import it
// from the same module as `PhoneE164StrictSchema`.
export { PhoneE164Schema } from "./ids/phone-e164";
export type { WhatsAppWaIdDigits } from "./ids/whatsapp-wa-id";

/**
 * Parsed phone number info.
 *
 * The string-level parsing/normalization is delegated to the shared
 * `@bavard/utils` implementation (one source of truth, shared with the server);
 * this module applies the api-client's brands on top for the web.
 */
export interface ParsedPhone {
	country?: string;
	e164: PhoneE164;
	lib: LibPhoneNumber;
	whatsappDigits: WhatsAppWaIdDigits;
}

/**
 * Strictly parse a phone number using the shared util and return canonical
 * E.164. Throws if the number is invalid.
 */
export function parsePhoneE164(input: string): ParsedPhone {
	const parsed = parsePhone(input);
	if (!parsed) {
		throw new Error("Invalid phone number format");
	}
	const lib = parsePhoneNumberFromString(parsed.e164);
	if (!lib) {
		throw new Error("Invalid phone number format");
	}
	return {
		lib,
		e164: createPhoneE164(parsed.e164),
		whatsappDigits: createWhatsAppWaIdDigits(parsed.whatsappDigits),
		country: parsed.country,
	};
}

/**
 * Normalize any phone string to canonical +E.164 format.
 */
export function normalizeToPhoneE164(input: string): PhoneE164 {
	const e164 = normalizePhoneToE164(input);
	if (e164 === null) {
		throw new Error("Invalid phone number format");
	}
	return createPhoneE164(e164);
}

/**
 * Convert canonical PhoneE164 to WhatsApp digits-only format.
 */
export function toWhatsAppWaIdDigits(
	phone: PhoneE164 | string
): WhatsAppWaIdDigits {
	const parsed = parsePhone(phone);
	if (!parsed) {
		throw new Error("Invalid phone number format");
	}
	return createWhatsAppWaIdDigits(parsed.whatsappDigits);
}

/**
 * Zod schema for canonical PhoneE164 with full libphonenumber-js validation.
 *
 * Use this schema when you need strict validation (e.g., country-code prefix
 * checks, regional rules). Prefer the regex-only `PhoneE164Schema` for fast
 * structural validation when the input is already trusted.
 */
export const PhoneE164StrictSchema = BrandedPhoneE164Schema.superRefine(
	(value, ctx) => {
		if (!isValidPhoneE164Plain(value)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Invalid phone number format",
			});
		}
	}
);

/**
 * Convenience predicate for checking if a string is a valid E.164 phone number.
 */
export function isValidPhoneE164(value: string | null | undefined): boolean {
	return isValidPhoneE164Plain(value);
}

/**
 * Format for display (already in +E.164, so just returns as is or ensures +).
 */
export function formatPhoneForDisplay(phone: PhoneE164 | string): string {
	const trimmed = phone.trim();
	if (!trimmed) {
		return "";
	}
	return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
}
